import { formatCompactThousands, toFullCurrency } from '../../utils/numberFormatters'

export const LOTE_VOLUME_TOOLTIP =
  'Volume calculado automaticamente (capacidade do veículo × quantidade). Altera conforme a quantidade de veículos.'

const parseMonetaryToNumber = (formatted) =>
  Number(String(formatted || '').replace(/\D/g, '')) / 100

const formatTon = (value) => {
  if (!value && value !== 0) return '-'
  return `${value} ton`
}

const formatFooterTon = (value) => {
  if (!value && value !== 0) return '0 Ton'
  return `${value} Ton`
}

const getCapacityTon = (vehicleType, config) =>
  config?.vehicleCapacity
  ?? config?.vehicleTypeAntt?.vehicleCapacity
  ?? config?.vehicleTypeAntt?.capacityWeightByTon
  ?? vehicleType?.vehicleCapacity
  ?? vehicleType?.capacityWeightByTon
  ?? 0

export { getCapacityTon }

const getVehicleTypeLabel = (vehicleType) => {
  const name = vehicleType?.name || '-'
  const axes = vehicleType?.numberLoadedAxes
  if (!axes) return name
  return `${name} (${axes} eixos)`
}

const getVehicleTypeNameFromPart = (part) => {
  const vehicleType = part?.negotiationConfig?.vehicleTypeAntt
  return vehicleType?.name || '-'
}

export const formatLoteMonetaryValue = (value) => {
  if (value === undefined || value === null) return '-'

  const formatted = toFullCurrency(value)
  if (!formatted) return '-'
  return `${formatted} /ton`
}

export const formatLoteTotal = (value) => {
  if (value === undefined || value === null) return '-'
  return toFullCurrency(value) || '-'
}

export const formatProposalMarginDisplay = (percentMargin) => {
  const value = percentMargin != null && typeof percentMargin === 'object'
    ? percentMargin.percentMargin
    : percentMargin

  if (value === undefined || value === null || value === '') return null
  if (typeof value === 'object') return null
  return `${value}%`
}

export const isLoteMarginNegotiation = (negotiateByMargin, percentMargin) => {
  const value = percentMargin != null && typeof percentMargin === 'object'
    ? percentMargin.percentMargin
    : percentMargin

  return negotiateByMargin
    || (value !== undefined && value !== null)
}

export const mapProposalStatus = (status) => {
  if (status == null) return null
  const normalized = String(status).toLowerCase()
  if (normalized === 'accepted' || normalized === 'approved') return 'accepted'
  if (normalized === 'refused') return 'refused'
  return null
}

const isRefusedPart = (part) => mapProposalStatus(part.status) === 'refused'

const isAcceptedPart = (part) => mapProposalStatus(part.status) === 'accepted'

export const isRefusedProposalStatus = (proposalStatus) => proposalStatus === 'refused'

export const isAcceptedProposalStatus = (proposalStatus) => proposalStatus === 'accepted'

export const isLockedProposalStatus = (proposalStatus) =>
  isAcceptedProposalStatus(proposalStatus) || isRefusedProposalStatus(proposalStatus)

export const indexNegotiationConfigsByVehicleId = (negotiationConfigs) => {
  const configsByVehicleId = {}

  ;(negotiationConfigs ?? []).forEach((config) => {
    const vehicleTypeId = config?.vehicleTypeAntt?.id
    if (vehicleTypeId == null) return
    if (config.status && config.status !== 'ENABLE') return
    configsByVehicleId[vehicleTypeId] = config
  })

  return configsByVehicleId
}

export const buildBubbleLines = (proposalItem) =>
  (proposalItem?.itensParts ?? []).map((part) => {
    const isRefused = isRefusedPart(part)
    const isAccepted = isAcceptedPart(part)
    return {
      id: part.id,
      name: getVehicleTypeNameFromPart(part),
      value: isRefused ? 'recusado' : formatLoteMonetaryValue(part.value),
      isRefused,
      isAccepted,
    }
  })

export const mapItensPartsToVehicleProposals = (proposalItem) =>
  (proposalItem?.itensParts ?? []).map((part) => ({
    id: part.id,
    vehicleTypeAnttId: part.negotiationConfig?.vehicleTypeAntt?.id,
    vehicleTypeAntt: part.negotiationConfig?.vehicleTypeAntt,
    qtd: part.availableQuantity,
    availableQuantity: part.availableQuantity,
    value: part.value,
    freightPriceMin: part.negotiationConfig?.freightPriceMin,
    tollPrice: part.negotiationConfig?.tollPrice,
    icmsPrice: part.icmsPrice ?? part.negotiationConfig?.icmsPrice,
    marginValue: part.marginValue,
    total: part.value,
    status: part.status,
    percentMargin: part.percentMargin != null
      ? part.percentMargin
      : (part.percentage != null ? part.percentage : proposalItem.percentMargin),
    reason: typeof part.reason === 'string' ? part.reason.trim() : undefined,
  }))

export const buildInitialLoteRows = (
  vehicleTypes,
  configsById,
  lastQuantitiesByVehicleId = {},
  lastPercentMargin = null,
  lastValuesByVehicleId = {},
  lockedPartsByVehicleId = {},
  lastPercentMarginsByVehicleId = {},
) => vehicleTypes.map((vehicleType) => {
  const config = configsById[vehicleType.id]
  const capacityTon = getCapacityTon(vehicleType, config)
  const lockedPart = lockedPartsByVehicleId[vehicleType.id]
  const isLocked = isLockedProposalStatus(lockedPart?.status)

  const quantity = isLocked ? lockedPart.availableQuantity : lastQuantitiesByVehicleId[vehicleType.id]
  const value = isLocked ? lockedPart.value : lastValuesByVehicleId[vehicleType.id]
  const margin = isLocked && lockedPart.percentMargin != null
    ? String(lockedPart.percentMargin)
    : lastPercentMargin != null ? String(lastPercentMargin) : ''
  const partPercentMargin = isLocked
    ? (lockedPart.percentMargin ?? null)
    : (lastPercentMarginsByVehicleId[vehicleType.id] ?? null)

  return {
    key: String(vehicleType.id),
    vehicleTypeId: vehicleType.id,
    typeName: vehicleType.name,
    numberLoadedAxes: vehicleType.numberLoadedAxes,
    configId: config?.id ?? null,
    capacityTon,
    qtd: quantity != null ? String(quantity) : '',
    margin,
    valuePerTon: value != null ? (toFullCurrency(value) || '') : '',
    proposalStatus: isLocked ? lockedPart.status : null,
    icmsLoading: false,
    icmsError: false,
    rawMinFreight: (isLocked ? lockedPart.freightPriceMin : null) ?? config?.freightPriceMin ?? 0,
    rawToll: (isLocked ? lockedPart.tollPrice : null) ?? config?.tollPrice ?? 0,
    rawIcms: (isLocked ? lockedPart.icmsPrice : null) ?? config?.icmsPrice ?? 0,
    configRawMinFreight: config?.freightPriceMin ?? 0,
    configRawToll: config?.tollPrice ?? 0,
    configRawIcms: config?.icmsPrice ?? 0,
    rawTotal: isLocked ? (lockedPart.value ?? null) : null,
    rawMarginValue: isLocked ? (lockedPart.marginValue ?? null) : null,
    partPercentMargin,
    rawPercentMargin: partPercentMargin,
    reason: isLocked
      ? (typeof lockedPart.reason === 'string' ? lockedPart.reason.trim() : '')
      : '',
  }
})

const buildVolumeTon = (qtd, capacityTon) => {
  const parsedQtd = Number(qtd)
  if (!qtd || Number.isNaN(parsedQtd) || parsedQtd <= 0) return 0
  return parsedQtd * capacityTon
}

const withTonSuffix = (formatted) => {
  if (!formatted || formatted === '-') return formatted
  return `${formatted} /ton`
}

const buildFooterMoneyValue = (amount) => {
  const fullCurrency = toFullCurrency(amount) || 'R$ 0,00'
  const abs = Math.abs(Number(amount) || 0)

  if (abs < 1000) {
    return { display: fullCurrency }
  }

  return {
    display: formatCompactThousands(amount),
    tooltip: fullCurrency,
  }
}

const formatRatePerTon = (rate) => {
  if (rate == null || Number.isNaN(Number(rate))) return '-'
  return withTonSuffix(toFullCurrency(rate) || 'R$ 0,00')
}

export const buildGridRowsFromState = (rowStates, negotiateByMargin) =>
  rowStates.map((row) => {
    const volumeTon = buildVolumeTon(row.qtd, row.capacityTon)
    const cells = {
      type: getVehicleTypeLabel({
        name: row.typeName,
        numberLoadedAxes: row.numberLoadedAxes,
      }),
      qtd: row.qtd,
      capacidade: formatTon(row.capacityTon),
      volume: volumeTon ? formatTon(volumeTon) : '-',
      min_freight: formatRatePerTon(row.rawMinFreight),
    }

    if (negotiateByMargin) {
      cells.margin = row.margin ?? ''
      cells.toll = formatRatePerTon(row.rawToll)
      cells.icms = formatRatePerTon(row.rawIcms)
      cells.total_freight = row.rawTotal != null
        ? formatRatePerTon(row.rawTotal)
        : '-'
    } else {
      cells.valuePerTon = row.valuePerTon ?? ''
      cells.toll = formatRatePerTon(row.rawToll)
      cells.icms = formatRatePerTon(row.rawIcms)
      cells.margin = row.icmsLoading
        ? '...'
        : row.icmsError
          ? '-'
          : (row.rawPercentMargin != null
            ? formatProposalMarginDisplay(row.rawPercentMargin)
            : '-')
    }

    const reason = typeof row.reason === 'string' ? row.reason.trim() : ''
    if (reason) {
      cells.reason = reason
    }

    return {
      key: row.key,
      proposalStatus: row.proposalStatus,
      cells,
    }
  })
export const updateLoteRowField = (rowStates, rowKey, field, value) =>
  rowStates.map((row) => {
    if (row.key !== rowKey) return row
    if (isLockedProposalStatus(row.proposalStatus)) return row

    return {
      ...row,
      [field]: value,
    }
  })

export const isRowValueEmpty = (row) => {
  const value = roundToTwoDecimalPlaces(parseMonetaryToNumber(row.valuePerTon))
  return value <= 0
}

export const resetRowIcmsToConfigDefaults = (row, config) => ({
  ...row,
  icmsLoading: false,
  icmsError: false,
  rawMinFreight: config?.freightPriceMin ?? row.configRawMinFreight ?? 0,
  rawToll: config?.tollPrice ?? row.configRawToll ?? 0,
  rawIcms: config?.icmsPrice ?? row.configRawIcms ?? 0,
  rawTotal: null,
  rawMarginValue: null,
  rawPercentMargin: row.partPercentMargin ?? null,
})

export const getInvalidValuePerTonRowKeys = (rowStates) =>
  rowStates
    .filter((row) =>
      !isLockedProposalStatus(row.proposalStatus)
      && isRowQuantityValid(row)
      && (isRowValueEmpty(row) || isRowValueBelowMinimum(row)),
    )
    .map((row) => row.key)

export const applyValuePerTonFieldChange = (rowStates, rowKey, value, configByVehicleId) => {
  const withField = updateLoteRowField(rowStates, rowKey, 'valuePerTon', value)

  return withField.map((row) => {
    if (row.key !== rowKey || isLockedProposalStatus(row.proposalStatus)) return row

    const config = configByVehicleId?.[row.vehicleTypeId]
    if (isRowValueEmpty(row) || isRowValueBelowMinimum(row)) {
      return resetRowIcmsToConfigDefaults(row, config)
    }

    return row
  })
}

export const applyIcmsToRowState = (rowStates, rowKey, icmsResult, icmsLoading, icmsError) =>
  rowStates.map((row) => {
    if (row.key !== rowKey) return row
    if (isLockedProposalStatus(row.proposalStatus)) return row

    if (icmsLoading || icmsError) {
      return {
        ...row,
        icmsLoading: !!icmsLoading,
        icmsError: !!icmsError,
      }
    }

    if (!icmsResult || icmsResult.total == null) {
      return resetRowIcmsToConfigDefaults(row)
    }

    return {
      ...row,
      icmsLoading: false,
      icmsError: false,
      rawMinFreight: icmsResult.freightPriceMin ?? row.rawMinFreight,
      rawToll: icmsResult.tollPrice ?? row.rawToll,
      rawIcms: icmsResult.icmsPrice ?? row.rawIcms,
      rawTotal: icmsResult.total ?? row.rawTotal,
      rawMarginValue: icmsResult.marginValue ?? row.rawMarginValue,
      rawPercentMargin: icmsResult.percentMargin ?? row.partPercentMargin ?? row.rawPercentMargin,
    }
  })

export const calculateLoteFooterTotals = (
  rowStates,
  negotiateByMargin,
  loadWeightTon = null,
) => {
  if (rowStates.length > 0 && rowStates.every((row) => isRefusedProposalStatus(row.proposalStatus))) {
    return null
  }

  const allAccepted = rowStates.length > 0
    && rowStates.every((row) => isAcceptedProposalStatus(row.proposalStatus))

  let volumeTotal = 0
  let acceptedVolumeTon = 0
  let minFreightTotal = 0
  let tollTotal = 0
  let icmsTotal = 0
  let proposalTotal = 0

  const accumulateRowTotals = (row, volumeTon) => {
    volumeTotal += volumeTon
    minFreightTotal += (row.rawMinFreight || 0) * volumeTon
    tollTotal += (row.rawToll || 0) * volumeTon
    icmsTotal += (row.rawIcms || 0) * volumeTon

    const ratePerTon = negotiateByMargin
      ? (row.rawTotal || 0)
      : parseMonetaryToNumber(row.valuePerTon)

    proposalTotal += ratePerTon * volumeTon
  }

  rowStates.forEach((row) => {
    if (isRefusedProposalStatus(row.proposalStatus)) return

    const qtd = Number(row.qtd)
    if (!row.qtd || Number.isNaN(qtd) || qtd <= 0) return

    const volumeTon = buildVolumeTon(row.qtd, row.capacityTon)

    if (isAcceptedProposalStatus(row.proposalStatus)) {
      if (allAccepted) {
        accumulateRowTotals(row, volumeTon)
        return
      }

      acceptedVolumeTon += volumeTon
      return
    }

    accumulateRowTotals(row, volumeTon)
  })

  const lotWeightTon = Number(loadWeightTon) || 0
  const weightTon = allAccepted
    ? lotWeightTon
    : Math.max(0, lotWeightTon - acceptedVolumeTon)
  const freightPerTon = volumeTotal > 0 ? proposalTotal / volumeTotal : 0
  const marginValueTotal = proposalTotal - minFreightTotal - tollTotal - icmsTotal
  const marginAverage = !negotiateByMargin && minFreightTotal > 0 && proposalTotal > 0
    ? Number(((marginValueTotal / minFreightTotal) * 100).toFixed(2))
    : null

  return {
    volumeTotal: formatFooterTon(volumeTotal || 0),
    loteWeight: formatFooterTon(weightTon),
    weightLabel: allAccepted ? 'lot' : 'available',
    minFreightTotal: buildFooterMoneyValue(minFreightTotal),
    tollTotal: buildFooterMoneyValue(tollTotal),
    icmsTotal: buildFooterMoneyValue(icmsTotal),
    proposalTotal: buildFooterMoneyValue(proposalTotal),
    freightTotal: negotiateByMargin ? buildFooterMoneyValue(freightPerTon) : undefined,
    marginTotal: !negotiateByMargin
      ? { display: marginAverage != null ? formatProposalMarginDisplay(marginAverage) : '-' }
      : undefined,
  }
}

/** Comparisons ignore precision beyond 2 decimal places (same as UI currency display). */
const roundToTwoDecimalPlaces = (value) =>
  Number((Number(value) || 0).toFixed(2))

const calculateMinimumBase = (freightPriceMin, tollPrice, icmsPrice) =>
  roundToTwoDecimalPlaces(
    roundToTwoDecimalPlaces(freightPriceMin)
    + roundToTwoDecimalPlaces(tollPrice)
    + roundToTwoDecimalPlaces(icmsPrice),
  )

export const isTotalValueAtOrAboveMinimum = (
  total,
  freightPriceMin,
  tollPrice,
  icmsPrice,
) => {
  if (total <= 0) return false

  return roundToTwoDecimalPlaces(total)
    >= calculateMinimumBase(freightPriceMin, tollPrice, icmsPrice)
}

export const canFetchIcmsByTotal = (
  total,
  freightPriceMin,
  tollPrice,
  icmsPrice,
) => isTotalValueAtOrAboveMinimum(total, freightPriceMin, tollPrice, icmsPrice)

export const isValidTotalIcmsCalculation = (icmsCalculation) =>
  !!icmsCalculation
  && !icmsCalculation.loading
  && !icmsCalculation.error
  && icmsCalculation.total > 0

export const getRowConfigRates = (row) => ({
  freightPriceMin: row.configRawMinFreight ?? row.rawMinFreight ?? 0,
  tollPrice: row.configRawToll ?? row.rawToll ?? 0,
  icmsPrice: row.configRawIcms ?? row.rawIcms ?? 0,
})

export const isRowQuantityValid = (row) => {
  const qtd = Number(row.qtd)
  return !!(row.qtd && !Number.isNaN(qtd) && qtd > 0)
}

/** True when the row has a filled value that is below frete min + pedágio + ICMS (config rates). */
export const isRowValueBelowMinimum = (row) => {
  if (row.icmsLoading) return false

  const value = roundToTwoDecimalPlaces(parseMonetaryToNumber(row.valuePerTon))
  if (value <= 0) return false

  const { freightPriceMin, tollPrice, icmsPrice } = getRowConfigRates(row)
  return !isTotalValueAtOrAboveMinimum(value, freightPriceMin, tollPrice, icmsPrice)
}

export const getLoteOfferedCapacity = (rowStates, loadWeightTon) => {
  const acceptedVolumeTon = rowStates.reduce((sum, row) => {
    if (!isAcceptedProposalStatus(row.proposalStatus) || !isRowQuantityValid(row)) return sum
    return sum + buildVolumeTon(row.qtd, row.capacityTon)
  }, 0)

  const availableWeightTon = Math.max(0, (Number(loadWeightTon) || 0) - acceptedVolumeTon)

  const pendingRows = rowStates.filter((row) =>
    !isRefusedProposalStatus(row.proposalStatus)
    && !isAcceptedProposalStatus(row.proposalStatus)
    && isRowQuantityValid(row),
  )

  if (!pendingRows.length) {
    return {
      totalVolumeTon: 0,
      smallestCapacityTon: 0,
      surplusTon: 0,
      surplusFullVehicles: 0,
      maxAllowedTon: availableWeightTon,
      availableWeightTon,
      isWithinLimit: true,
    }
  }

  const totalVolumeTon = pendingRows.reduce(
    (sum, row) => sum + buildVolumeTon(row.qtd, row.capacityTon),
    0,
  )
  const smallestCapacityTon = Math.min(...pendingRows.map((row) => row.capacityTon))
  // Excess must be strictly less than the smallest vehicle (cannot leave one full unused vehicle).
  const maxAllowedTon = availableWeightTon + smallestCapacityTon
  const surplusTon = Math.max(0, totalVolumeTon - availableWeightTon)
  const surplusFullVehicles =
    smallestCapacityTon > 0 ? Math.floor(surplusTon / smallestCapacityTon) : 0

  return {
    totalVolumeTon,
    smallestCapacityTon,
    surplusTon,
    surplusFullVehicles,
    maxAllowedTon,
    availableWeightTon,
    isWithinLimit: totalVolumeTon < maxAllowedTon,
  }
}

const isRowValueValid = (row) => {
  if (!isRowQuantityValid(row)) return false
  if (row.icmsLoading || row.icmsError) return false

  const value = roundToTwoDecimalPlaces(parseMonetaryToNumber(row.valuePerTon))
  if (value <= 0) return false

  const { freightPriceMin, tollPrice, icmsPrice } = getRowConfigRates(row)
  if (!isTotalValueAtOrAboveMinimum(value, freightPriceMin, tollPrice, icmsPrice)) return false

  return row.rawTotal != null
}

export const isLoteSubmitValid = (rowStates, negotiateByMargin, percentMargin) => {
  const includedRows = rowStates.filter((row) =>
    !isRefusedProposalStatus(row.proposalStatus) && isRowQuantityValid(row),
  )
  if (!includedRows.length) return false

  const pendingRows = includedRows.filter((row) => !isLockedProposalStatus(row.proposalStatus))
  if (!pendingRows.length) return true

  if (negotiateByMargin) {
    const margin = Number(percentMargin)
    if (percentMargin === '' || percentMargin == null || Number.isNaN(margin) || margin < 0) {
      return false
    }
    if (pendingRows.some((row) => row.icmsLoading || row.icmsError)) return false
    return true
  }

  if (pendingRows.some((row) => row.icmsLoading || row.icmsError)) return false

  return pendingRows.every(isRowValueValid)
}

export const buildLoteSubmitPayload = ({
  transportType,
  urlRoute,
  estimatedDate,
  observation,
  rowStates,
  negotiateByMargin,
  percentMargin,
}) => {
  const validRows = rowStates.filter((row) => {
    if (!isRowQuantityValid(row)) return false
    if (isLockedProposalStatus(row.proposalStatus)) return false
    if (negotiateByMargin) {
      return !row.icmsLoading && !row.icmsError
    }
    return isRowValueValid(row) && !row.icmsLoading && !row.icmsError
  })

  const parts = validRows.map((row) => {
    const entry = {
      shippingNegotiationConfigId: row.configId,
      availableQuantity: Number(row.qtd),
    }

    if (!negotiateByMargin) {
      entry.value = parseMonetaryToNumber(row.valuePerTon)
    }

    return entry
  })

  const makeProposalLotByVehicleType = {
    parts,
    typeTransport: transportType,
    urlRoute,
  }

  if (negotiateByMargin) {
    makeProposalLotByVehicleType.percentMargin = Number(percentMargin)
  }

  return {
    estimatedDate,
    observation,
    makeProposalLotByVehicleType,
  }
}

export const buildLoteViewRows = (vehicleProposals, configsById, negotiateByMargin) =>
  (vehicleProposals || []).map((proposal) => {
    const vehicleTypeId = proposal.vehicleTypeAnttId
      ?? proposal.vehicleTypeAntt?.id
    const config = configsById[vehicleTypeId]
    const vehicleType = proposal.vehicleTypeAntt || config?.vehicleTypeAntt
    const capacityTon = getCapacityTon(vehicleType, config)
    const qtd = proposal.availableQuantity ?? proposal.qtd ?? ''
    const volumeTon = buildVolumeTon(String(qtd), capacityTon)

    const freightPriceMin = proposal.freightPriceMin ?? config?.freightPriceMin
    const tollPrice = proposal.tollPrice ?? config?.tollPrice
    const icmsPrice = proposal.icmsPrice ?? config?.icmsPrice
    const totalFreight = proposal.total ?? proposal.value

    const cells = {
      type: getVehicleTypeLabel(vehicleType),
      qtd: String(qtd || '-'),
      capacidade: formatTon(capacityTon),
      volume: volumeTon ? formatTon(volumeTon) : '-',
      min_freight: formatRatePerTon(freightPriceMin),
      toll: formatRatePerTon(tollPrice),
      icms: formatRatePerTon(icmsPrice),
    }

    if (negotiateByMargin) {
      cells.margin = proposal.percentMargin != null ? `${proposal.percentMargin}%` : '-'
      cells.total_freight = formatRatePerTon(totalFreight)
    } else {
      cells.valuePerTon = proposal.value != null
        ? formatLoteMonetaryValue(proposal.value)
        : '-'
      cells.margin = proposal.percentMargin != null
        ? formatProposalMarginDisplay(proposal.percentMargin)
        : '-'
    }

    const reason = typeof proposal.reason === 'string' ? proposal.reason.trim() : ''
    if (reason) {
      cells.reason = reason
    }

    return {
      key: String(vehicleTypeId || proposal.id),
      proposalStatus: mapProposalStatus(proposal.status ?? proposal.proposalStatus),
      cells,
    }
  })

/** Numeric row states used by calculateLoteFooterTotals in the view drawer. */
export const buildLoteViewFooterRowStates = (vehicleProposals, configsById, negotiateByMargin) =>
  (vehicleProposals || []).map((proposal) => {
    const vehicleTypeId = proposal.vehicleTypeAnttId
      ?? proposal.vehicleTypeAntt?.id
    const config = configsById[vehicleTypeId]
    const vehicleType = proposal.vehicleTypeAntt || config?.vehicleTypeAntt
    const capacityTon = getCapacityTon(vehicleType, config)
    const qtd = proposal.availableQuantity ?? proposal.qtd ?? ''

    return {
      key: String(vehicleTypeId || proposal.id),
      proposalStatus: mapProposalStatus(proposal.status ?? proposal.proposalStatus),
      qtd: String(qtd || ''),
      capacityTon,
      valuePerTon: !negotiateByMargin && proposal.value != null
        ? (toFullCurrency(proposal.value) || '')
        : '',
      rawMinFreight: proposal.freightPriceMin ?? config?.freightPriceMin ?? 0,
      rawToll: proposal.tollPrice ?? config?.tollPrice ?? 0,
      rawIcms: proposal.icmsPrice ?? config?.icmsPrice ?? 0,
      rawTotal: negotiateByMargin ? (proposal.total ?? null) : null,
      rawPercentMargin: !negotiateByMargin ? (proposal.percentMargin ?? null) : null,
    }
  })

export const applyIcmsRatesToFooterRowStates = (rowStates, rowKey, rates) =>
  rowStates.map((row) => {
    if (row.key !== rowKey || rates.loading || rates.error) return row
    if (isLockedProposalStatus(row.proposalStatus)) return row

    return {
      ...row,
      rawMinFreight: rates.rawMinFreight ?? row.rawMinFreight,
      rawToll: rates.rawToll ?? row.rawToll,
      rawIcms: rates.rawIcms ?? row.rawIcms,
      rawTotal: rates.rawTotal ?? row.rawTotal,
      rawPercentMargin: rates.percentMargin ?? row.rawPercentMargin,
    }
  })

export const getSharedRouteFromConfigs = (configsById) => {
  const configs = Object.values(configsById).filter(Boolean)
  if (!configs.length) return { distance: 0, urlRoute: '' }
  return {
    distance: configs[0].distance,
    urlRoute: configs[0].urlRoute,
  }
}

const getRawProposalParts = (item) => {
  if (!item) return []

  const fromParts = item.itensParts ?? []
  const fromPart = item.itensPart ?? []

  if (!fromParts.length) return fromPart
  if (!fromPart.length || fromParts === fromPart) return fromParts
  return [...fromParts, ...fromPart]
}

const parseNegotiationDateForLatest = (value) => {
  if (!value) return 0

  const [datePart, timePart = '00:00:00'] = String(value).split(' ')
  const [day, month, year] = datePart.split('/')
  if (year && month && day) {
    return new Date(`${year}-${month}-${day}T${timePart}`).getTime() || 0
  }

  return Date.parse(value) || 0
}

const pickLatestByDate = (items) =>
  items.reduce(
    (latest, item) =>
      (!latest || parseNegotiationDateForLatest(item.date) >= parseNegotiationDateForLatest(latest.date)
        ? item
        : latest),
    null,
  )

export const getLastLoteNegotiationWithParts = (negotiations) => {
  if (!negotiations?.length) return null

  const withParts = (negotiations || []).filter((item) => getRawProposalParts(item).length)
  const transporters = withParts.filter((item) => item.agent === 'TRANSPORTER' && !item.firstPurpose)

  if (transporters.length) return pickLatestByDate(transporters)

  return pickLatestByDate(withParts)
}

export const getLoteProposalSummary = (negotiations) => {
  const emptySummary = {
    vehicleCount: null,
    vehicleCountDisplay: '-',
    volumeDisplay: '-',
    proposalDisplay: '-',
    transportTypeDisplay: '-',
    distance: null,
  }

  const negotiation = getLastLoteNegotiationWithParts(negotiations)
  return getLoteProposalSummaryFromItem(negotiation) ?? emptySummary
}

export const getLoteProposalSummaryFromItem = (negotiation) => {
  const emptySummary = {
    vehicleCount: null,
    vehicleCountDisplay: '-',
    volumeDisplay: '-',
    proposalDisplay: '-',
    transportTypeDisplay: '-',
    distance: null,
  }

  if (!negotiation) return emptySummary

  const parts = (negotiation.itensParts ?? []).filter((part) => !isRefusedPart(part))
  const vehicleCount = parts.reduce(
    (sum, part) => sum + (Number(part.availableQuantity) || 0),
    0,
  )

  const volumeTon = negotiation.itensPartsTotals?.totalCapacityWeightByTon
  const proposalTotal = negotiation.value
  const distance = parts[0]?.negotiationConfig?.distance ?? null

  return {
    vehicleCount: vehicleCount > 0 ? vehicleCount : null,
    vehicleCountDisplay: vehicleCount > 0 ? String(vehicleCount) : '-',
    volumeDisplay: volumeTon != null ? formatTon(volumeTon) : '-',
    proposalDisplay: proposalTotal != null
      ? formatLoteTotal(proposalTotal)
      : '-',
    transportTypeDisplay: negotiation.typeTransport || '-',
    distance,
  }
}
